import { FluentBundle } from 'fluent';
import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import {
  CountryProfile,
  CountryYear,
  Location,
  PolicyRecommendation,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import Loading from '../../sharedComponents/GraphLoading';
import { getCountryName } from '../../Utils';
import usePromotableZIndex from '../usePromotableZIndex';
import {
  Datum as ChartDatum,
} from './Chart';
import Chart from './Chart';
import {
  Root,
} from './Grid';

const query = gql`
  query RockSongChart($id: ID!, $year: Int!) {
    allLocations(level: country) {
      id
      shortName
      isInCountryPages
    }
    allCountryProfiles {
      location {
        id
      }
      eci: eciNatResourcesGdpControlled
      policyRecommendation
    }
    allCountryYear(year: $year) {
      location {
        id
      }
      coi
    }
    focusedCountry: location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
  }
`;

interface Variables {
  id: string;
  year: number;
}

interface SuccessResponse {
  allLocations: Array<{
    id: Location['id'];
    shortName: Location['shortName'];
    isInCountryPages: Location['isInCountryPages'];
  }>;
  allCountryProfiles: Array<{
    location: {id: Location['id']};
    eci: CountryProfile['eciNatResourcesGdpControlled'];
    policyRecommendation: CountryProfile['policyRecommendation'];
  }>;
  allCountryYear: Array<{
    location: {id: Location['id']};
    coi: CountryYear['coi'];
  }>;
  focusedCountry: null | {
    id: Location['id']
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'];
    thePrefix: Location['thePrefix'];
  };
}

const transform = (input: SuccessResponse) => {
  const result: ChartDatum[] = [];
  for (const {id, shortName, isInCountryPages} of input.allLocations) {
    const countryProfilesDatum = input.allCountryProfiles.find(d => d.location.id === id);
    const countryYearDatum = input.allCountryYear.find(d => d.location.id === id);
    const eci = countryProfilesDatum ? countryProfilesDatum.eci : null;
    const policyRecommendation = countryProfilesDatum ? countryProfilesDatum.policyRecommendation : null;
    const coi = countryYearDatum ? countryYearDatum.coi : null;
    if (eci !== null && coi !== null && policyRecommendation !== null && isInCountryPages === true) {
      const chartDatum: ChartDatum = {
        id, eci, coi,
        label: shortName,
        policy: policyRecommendation,
      };
      result.push(chartDatum);
    }
  }
  return result;
};

const zIndices = {
  leftLegendContainer: 500,
  bottomLegendContainer: 510,
  xAxisLabelContainer: 520,
  xTicksElem: 530,
  yTicksElem: 540,
  gridlines: 550,
  chartContainer: 560,
  quadrantLabelsContainer: 570,
  tooltipsContainer: 580,
  yAxisLabelContainer: 590,
};

const promotedZIndex = 3000;

interface Props extends SlideElementProps {
  countryId: string;
  getFluentString: GetString;
  year: number;
  zIndex: number;
  fluentBundle: FluentBundle;
  openFlowChart: (value: PolicyRecommendation | null) => void;
}
const spinnerKey = 'spinner';
const chartKey = 'chart';

const Wrapper = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    countryId, getFluentString, year, zIndex,
    fluentBundle, openFlowChart,
  } = props;
  const variables: Variables = {
    id: countryId,
    year,
  };

  const {
    getZIndex,
  } = usePromotableZIndex({
    normalZIndices: zIndices,
    promotedZIndex,
  });

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = (
      <>
        <Loading key={spinnerKey}/>
      </>
    );
  } else if (error !== undefined) {
    output = null;
    console.error(error);
  } else if (data !== undefined) {
    const {focusedCountry} = data;
    if (focusedCountry === null) {
      output = null;
    } else {
      const chartData = transform(data);
      const {nameSecondary} = getCountryName({
        shortName: focusedCountry.shortName,
        nameAbbr: focusedCountry.nameAbbr,
        thePrefix: Boolean(focusedCountry.thePrefix),
      });
      output = (
        <>
          <Chart
            data={chartData}
            highlighted={countryId}
            getFluentString={getFluentString}
            countryName={nameSecondary}
            minX={-2}
            maxX={1.5}
            minY={-3}
            maxY={3.5}
            key={chartKey}
            fluentBundle={fluentBundle}
            getZIndex={getZIndex}
            openFlowChart={openFlowChart}
          />
        </>
      );
    }
  } else {
    output = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <Root style={style} ref={rootElRef}>
      {output}
    </Root>
  );
});

export default Wrapper;
